import { useEffect, useState } from 'react';
import { THEME_CSS, THEME_STORAGE_KEY, ThemeName, ThemePref } from 'shared/lib/adminTheme';

/**
 * The admin's light/dark choice: kept in this browser (localStorage), applied
 * as `data-gx-theme` on the root element. The page head applies it before the
 * app starts (THEME_INIT_SCRIPT); this keeps it in step afterwards.
 */

const PREFS: ThemePref[] = ['light', 'dark', 'system'];

export const readThemePref = (): ThemePref => {
  try {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY) as ThemePref | null;
    return saved && PREFS.includes(saved) ? saved : 'light';
  } catch (e) {
    return 'light';
  }
};

const systemDark = (): boolean => {
  try {
    return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  } catch (e) {
    return false;
  }
};

export const resolveTheme = (pref: ThemePref): ThemeName => (pref === 'dark' || (pref === 'system' && systemDark()) ? 'dark' : 'light');

const apply = (pref: ThemePref) => {
  document.documentElement.setAttribute('data-gx-theme', resolveTheme(pref));
};

const listeners = new Set<(pref: ThemePref) => void>();

/** Saves the choice in this browser and switches every open screen to it. */
export const setThemePref = (pref: ThemePref) => {
  try { window.localStorage.setItem(THEME_STORAGE_KEY, pref); } catch (e) { /* not remembered, still applied */ }
  apply(pref);
  listeners.forEach(l => l(pref));
};

/**
 * Once, when the app starts: re-applies the saved choice, follows the
 * computer's light/dark setting while "system" is chosen (on every screen,
 * the editor included), and picks up a change made in another tab.
 */
export const startThemeSync = () => {
  // The server writes the theme's CSS variables into the page head. A page from a server that doesn't yet
  // (an older one during a deploy) would leave every color undefined, so the app adds them itself.
  if (!document.getElementById('gx-admin-theme')) {
    const style = document.createElement('style');
    style.id = 'gx-admin-theme';
    style.textContent = THEME_CSS;
    document.head.appendChild(style);
  }
  apply(readThemePref());
  try {
    const mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
    const onSystem = () => { if (readThemePref() === 'system') apply('system'); };
    if (mq) { if (mq.addEventListener) mq.addEventListener('change', onSystem); else mq.addListener(onSystem); }
  } catch (e) { /* no matchMedia: light or dark only */ }
  window.addEventListener('storage', (e) => {
    if (e.key !== THEME_STORAGE_KEY) return;
    const next = readThemePref();
    apply(next);
    listeners.forEach(l => l(next));
  });
};

/** The current choice and a setter, for the theme switch. */
export const useThemePref = (): [ThemePref, (pref: ThemePref) => void] => {
  const [pref, setPref] = useState<ThemePref>(readThemePref);
  useEffect(() => {
    listeners.add(setPref);
    return () => { listeners.delete(setPref); };
  }, []);
  return [pref, setThemePref];
};
